import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, Info, CheckCircle, AlertTriangle, XCircle,
  RefreshCw, Filter, ChevronLeft, ChevronRight,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  NotificationService,
  Notification,
} from "../../services/notifications/notificationService";
import toast from "react-hot-toast";

/* ── Icône par type ─────────────────────────────────────────────── */
const TypeIcon: React.FC<{ type: Notification["type"]; size?: string }> = ({
  type, size = "h-5 w-5",
}) => {
  switch (type) {
    case "success": return <CheckCircle className={`${size} text-accent-500`} />;
    case "warning": return <AlertTriangle className={`${size} text-warning-500`} />;
    case "error":   return <XCircle className={`${size} text-error-500`} />;
    default:        return <Info className={`${size} text-primary-500`} />;
  }
};

const TYPE_BG: Record<Notification["type"], string> = {
  success: "bg-accent-50",
  warning: "bg-warning-50",
  error:   "bg-error-50",
  info:    "bg-primary-50",
};

const FILTERS = [
  { key: "all",     label: "Toutes" },
  { key: "unread",  label: "Non lues" },
  { key: "info",    label: "Info" },
  { key: "success", label: "Succès" },
  { key: "warning", label: "Avertissements" },
  { key: "error",   label: "Erreurs" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

const PAGE_SIZE = 15;

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(1);
  const [filter, setFilter]               = useState<FilterKey>("all");
  const [loading, setLoading]             = useState(true);
  const [refreshing, setRefreshing]       = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const unreadCount = notifications.filter((n) => !n.lue).length;

  /* ── Fetch ───────────────────────────────────────────────────── */
  const fetchPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const data = await NotificationService.getNotifications(p);
      setNotifications(data.results);
      setTotalCount(data.count);
    } catch {
      toast.error("Impossible de charger les notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPage(page);
    setRefreshing(false);
  };

  /* ── Filtrage client-side ────────────────────────────────────── */
  const filtered = notifications.filter((n) => {
    if (filter === "all")    return true;
    if (filter === "unread") return !n.lue;
    return n.type === filter;
  });

  /* ── Actions ─────────────────────────────────────────────────── */
  const markRead = async (id: string) => {
    try {
      await NotificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lue: true } : n))
      );
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const markAllRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, lue: true })));
      toast.success("Toutes les notifications marquées comme lues.");
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const handleNotifClick = (notif: Notification) => {
    if (!notif.lue) markRead(notif.id);
    if (notif.lien) navigate(notif.lien);
  };

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">

      {/* ── En-tête ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCount} notification{totalCount !== 1 ? "s" : ""}
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700">
                {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600
                       border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </button>

          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
              style={{ backgroundColor: "#1E3A8A" }}
            >
              <Check className="h-4 w-4" />
              Tout marquer lu
            </button>
          )}
        </div>
      </div>

      {/* ── Filtres ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border
              ${filter === key
                ? "text-white border-transparent"
                : "text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white"
              }`}
            style={filter === key ? { backgroundColor: "#1E3A8A", borderColor: "#1E3A8A" } : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Liste ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400">
            <Bell className="h-12 w-12 mb-3 opacity-20" />
            <p className="text-base font-medium text-gray-500">Aucune notification</p>
            <p className="text-sm text-gray-400 mt-1">
              {filter === "all"
                ? "Vous n'avez aucune notification pour le moment."
                : "Aucune notification correspond à ce filtre."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`w-full text-left flex items-start gap-4 px-5 py-4 transition-colors group
                  ${!notif.lue ? "bg-primary-50/40 hover:bg-primary-50" : "hover:bg-gray-50"}`}
              >
                {/* Icône */}
                <div className={`mt-0.5 p-2.5 rounded-xl flex-shrink-0 ${TYPE_BG[notif.type]}`}>
                  <TypeIcon type={notif.type} size="h-5 w-5" />
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <p className={`text-sm font-semibold ${!notif.lue ? "text-gray-900" : "text-gray-600"}`}>
                      {notif.titre}
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {format(new Date(notif.timestamp), "dd MMM yyyy · HH:mm", { locale: fr })}
                      </span>
                      {!notif.lue && (
                        <div className="h-2 w-2 rounded-full bg-accent-500 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                    {notif.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {formatDistanceToNow(new Date(notif.timestamp), {
                      addSuffix: true,
                      locale: fr,
                    })}
                    {notif.lien && (
                      <span className="ml-2 text-primary-600 font-medium group-hover:underline">
                        Voir le détail →
                      </span>
                    )}
                  </p>
                </div>

                {/* Bouton marquer lu */}
                {!notif.lue && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markRead(notif.id); }}
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 mt-1 p-1.5 rounded-lg
                               text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
                    title="Marquer comme lu"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Pagination ──────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} sur {totalPages} · {totalCount} notification{totalCount !== 1 ? "s" : ""}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = page <= 3
                ? i + 1
                : page >= totalPages - 2
                ? totalPages - 4 + i
                : page - 2 + i;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors
                    ${page === p
                      ? "text-white"
                      : "text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                  style={page === p ? { backgroundColor: "#1E3A8A" } : undefined}
                >
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
